import { deviceOf } from "./devices";
import { audioContext } from "./volume";

/**
 * Голосовое сообщение — как в Discord: запись с микрофона, её длительность и
 * волна громкости, которую лента рисует вместо обычного плеера.
 */
export interface VoiceTake {
  file: File;
  durationMs: number;
  /** Байт на столбик, base64 — в таком виде волну хранит сервер. */
  waveform: string;
}

/** Двадцать минут — потолок Discord; тот же держит сервер. */
export const VOICE_MAX_MS = 20 * 60 * 1000;
/** Короче — случайное нажатие, а не сообщение. */
export const VOICE_MIN_MS = 500;
/** Столбиков в волне: в строку ленты шире не влезает. */
const BARS = 64;
const STEP_MS = 50;

export interface VoiceRecording {
  /** Громкость по отсчётам с начала записи, 0…1 — для живой волны. */
  levels: () => readonly number[];
  elapsed: () => number;
  /** Готовая запись; `null` — вышла короче `VOICE_MIN_MS`. */
  stop: () => Promise<VoiceTake | null>;
  cancel: () => void;
}

/**
 * Пишет выбранный в настройках микрофон. Громкость снимается анализатором раз
 * в 50 мс — из этих отсчётов и складывается волна. `onLimit` зовётся, когда
 * запись дошла до потолка: остановить её — дело того, кто начал.
 */
export async function recordVoice(onLimit: () => void): Promise<VoiceRecording> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { ...deviceOf("microphone"), echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  });
  const stopTracks = () => {
    for (const track of stream.getTracks()) track.stop();
  };
  try {
    return await startRecording(stream, stopTracks, onLimit);
  } catch (error) {
    // Микрофон уже открыт: упавшая настройка не должна оставить его гореть.
    stopTracks();
    throw error;
  }
}

async function startRecording(
  stream: MediaStream,
  stopTracks: () => void,
  onLimit: () => void,
): Promise<VoiceRecording> {
  const context = audioContext();
  await context.resume().catch(() => {});
  const source = context.createMediaStreamSource(stream);
  const analyser = context.createAnalyser();
  analyser.fftSize = 1024;
  source.connect(analyser);
  const frame = new Float32Array(analyser.fftSize);

  // Chromium пишет Opus только в webm; Ogg — запасной для других движков.
  const mimeType = ["audio/webm;codecs=opus", "audio/ogg;codecs=opus", "audio/webm"].find((type) =>
    MediaRecorder.isTypeSupported(type),
  );
  const recorder = new MediaRecorder(stream, mimeType ? { mimeType, audioBitsPerSecond: 64_000 } : undefined);
  const parts: Blob[] = [];
  recorder.ondataavailable = (event) => {
    if (event.data.size > 0) parts.push(event.data);
  };

  const samples: number[] = [];
  const started = performance.now();
  let limited = false;
  const timer = window.setInterval(() => {
    analyser.getFloatTimeDomainData(frame);
    let sum = 0;
    for (const value of frame) sum += value * value;
    samples.push(Math.min(1, Math.sqrt(sum / frame.length) * 3));
    if (!limited && performance.now() - started >= VOICE_MAX_MS) {
      limited = true;
      onLimit();
    }
  }, STEP_MS);

  const release = () => {
    window.clearInterval(timer);
    source.disconnect();
    stopTracks();
  };

  recorder.start(1000);

  return {
    levels: () => samples,
    elapsed: () => performance.now() - started,
    cancel: () => {
      recorder.onstop = null;
      if (recorder.state !== "inactive") recorder.stop();
      release();
    },
    stop: () =>
      new Promise((resolve) => {
        const durationMs = Math.min(VOICE_MAX_MS, Math.round(performance.now() - started));
        recorder.onstop = () => {
          release();
          if (durationMs < VOICE_MIN_MS || parts.length === 0) {
            resolve(null);
            return;
          }
          const type = recorder.mimeType || "audio/webm";
          resolve({
            file: new File(parts, `voice-message.${type.includes("ogg") ? "ogg" : "webm"}`, { type }),
            durationMs,
            waveform: encodeWave(samples),
          });
        };
        if (recorder.state === "inactive") recorder.onstop(new Event("stop"));
        else recorder.stop();
      }),
  };
}

/**
 * Отсчёты громкости — в столбики: среднее по равным долям, растянутое так,
 * что самый громкий столбик — 255. Тихая запись так не выглядит пустой.
 */
export function encodeWave(samples: readonly number[], bars = BARS): string {
  const count = Math.max(1, Math.min(bars, samples.length));
  const values: number[] = [];
  for (let index = 0; index < count; index++) {
    const from = Math.floor((index * samples.length) / count);
    const to = Math.max(from + 1, Math.floor(((index + 1) * samples.length) / count));
    const slice = samples.slice(from, to);
    values.push(slice.length ? slice.reduce((sum, value) => sum + value, 0) / slice.length : 0);
  }
  const peak = Math.max(...values, 1e-6);
  const bytes = values.map((value) => Math.round((255 * value) / peak));
  return btoa(String.fromCharCode(...bytes));
}

/** Столбики волны, 0…1. Битая строка — пустая волна, а не падение ленты. */
export function decodeWave(waveform: string): number[] {
  try {
    return Array.from(atob(waveform), (char) => char.charCodeAt(0) / 255);
  } catch {
    return [];
  }
}
